import getHistory from '../../../lib/history';
import { POLYGON_EVENTS, getPolygonBounds } from '../lib';
import { BOUNDING_BOX_PADDINGS } from '../constants';
import { FILTERS } from '../../../constants';
import { serializeItemLocation } from '../../context/filters/helpers';
import { useStaticProps } from '../../context/static-props';

/**
 * Custom hook for handling completion and processing of drawn polygons.
 *
 * Manages the complete flow when a polygon is completed:
 * - Updates the URL with the polygon location parameter
 * - Handles browser navigation and history
 * - Centers the map on the drawn polygon
 * - Injects item location into the URL
 * - Saves session state for subsequent navigation
 *
 * @param {Object} params - Hook parameters
 * @param {google.maps.Map} params.mapInstance - Google Maps instance
 * @param {Function} [params.setPolygonTogglerState] - Function to update toggler state
 * @param {Function} [params.markItemLocationInjected] - Function to mark location injection
 * @param {Function} [params.saveLocationBeforePolygon] - Function to save location before polygon
 * @param {Function} [params.saveCenteredPolygonBounds] - Function to save centered bounds
 *
 * @returns {Object} Object with the following properties:
 * @returns {Function} returns.onPolygonComplete - Callback function to handle polygon completion
 *
 * @example
 * ```jsx
 * const { onPolygonComplete } = usePolygonComplete({
 *   mapInstance: mapRef.current,
 *   setPolygonTogglerState: setTogglerState,
 *   markItemLocationInjected: markInjected,
 *   saveLocationBeforePolygon: saveLocation,
 *   saveCenteredPolygonBounds: saveBounds
 * });
 *
 * // Use in drawing hook
 * usePolygonDrawing({
 *   map: mapInstance,
 *   drawable: isDrawing,
 *   onPolygonComplete
 * });
 * ```
 */
const usePolygonComplete = ({
  mapInstance,
  setPolygonTogglerState,
  markItemLocationInjected,
  saveLocationBeforePolygon,
  saveCenteredPolygonBounds,
}) => {
  const history = getHistory();
  const { deviceType } = useStaticProps();

  const { DRAWN } = POLYGON_EVENTS;

  /**
   * Handles the completion of a drawn polygon.
   *
   * Processes the completed polygon by updating the URL, centering the map,
   * and handling browser history navigation. The tracking event will be emitted
   * after the new results are obtained, similar to how other filters work.
   *
   * @param {string} polygonLocationHash - Simplified polygon location hash
   * @param {google.maps.Polygon} [polygon=null] - Drawn polygon instance
   */
  const onPolygonComplete = (polygonLocationHash, polygon = null) => {
    if (!polygonLocationHash || !mapInstance) {
      return;
    }

    const { pathname, search } = history.location;
    const params = new URLSearchParams(search);

    const hasItemLocation = /_item\*location_lat:/.test(pathname);
    const segments = pathname.split('/').filter(Boolean);
    const operation = segments[0];
    const category = segments[1];

    let newPathname = pathname;

    const bounds = mapInstance.getBounds();

    if (saveLocationBeforePolygon) {
      saveLocationBeforePolygon({ pathname, search, mapBounds: bounds });
    }

    if (!bounds) {
      return;
    }

    params.set(FILTERS.IDS.POLYGON_LOCATION, polygonLocationHash);

    if (setPolygonTogglerState) {
      setPolygonTogglerState(DRAWN);
    }

    if (polygon && mapInstance) {
      const polygonBounds = getPolygonBounds(polygon);

      if (polygonBounds) {
        const tempListener = mapInstance.addListener('bounds_changed', () => {
          window.google.maps.event.removeListener(tempListener);

          const centeredBounds = mapInstance.getBounds();

          if (centeredBounds && saveCenteredPolygonBounds) {
            saveCenteredPolygonBounds(centeredBounds);

            const centeredBoundsJson = centeredBounds.toJSON();
            const centeredItemLocation = serializeItemLocation(centeredBoundsJson);

            if (!hasItemLocation) {
              newPathname = `/${operation}/${category}/_DisplayType_M${centeredItemLocation}`;
            } else {
              newPathname = pathname.replace(/_item\*location_lat:[^/]+/, centeredItemLocation);
            }

            if (markItemLocationInjected) {
              markItemLocationInjected();
            }

            history.push({
              pathname: newPathname,
              search: `?${params.toString()}`,
            });
          }
        });

        mapInstance.fitBounds(polygonBounds, BOUNDING_BOX_PADDINGS[deviceType]);
      }
    } else {
      const boundsJson = bounds.toJSON();
      const itemLocation = serializeItemLocation(boundsJson);

      if (!hasItemLocation) {
        newPathname = `/${operation}/${category}/_DisplayType_M${itemLocation}`;
      } else {
        newPathname = pathname.replace(/_item\*location_lat:[^/]+/, itemLocation);
      }

      if (markItemLocationInjected) {
        markItemLocationInjected();
      }

      history.push({
        pathname: newPathname,
        search: `?${params.toString()}`,
      });
    }
  };

  return { onPolygonComplete };
};

export default usePolygonComplete;
